import * as React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchApi,
  makeResetCache,
  notifyError,
  notifyResponse,
  PageTitle,
  useCacheRecord,
  selectUser,
  UserState,
} from 'gossamer-front-end';
import { AdminCreateEventRequest, EventExtendedView, EventExtendedViewRow, Event, Ticket } from 'cb-model';
import { Button, Container } from 'react-bootstrap';
import { EventForm } from './form/EventForm';
import { convertEventToCopyRequest, defaultEventDetails } from './form/eventFormUtils';
import { CustomInfo, PERSONAL_INSTITUTION_ID } from 'cb-unified';

export const EventCreate = (): React.JSX.Element => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetCache = makeResetCache(dispatch);
  const [searchParams] = useSearchParams();
  const copyFrom = searchParams.get('copyFrom');

  const user = useSelector((state: { user: UserState }) => selectUser(state));
  const institutionId =
    (user.customInfo as CustomInfo | undefined)?.createEventInstitutionId ?? PERSONAL_INSTITUTION_ID;

  const copyFromRecord = useCacheRecord<EventExtendedViewRow>(EventExtendedView, copyFrom, !copyFrom);

  const [submitting, setSubmitting] = React.useState(false);
  const [value, setValue] = React.useState<AdminCreateEventRequest>(() => defaultEventDetails());

  React.useEffect(() => {
    if (!copyFrom) return;
    if (!copyFromRecord.data) return;
    setValue(convertEventToCopyRequest(copyFromRecord.data));
  }, [copyFrom, copyFromRecord.data]);

  const handleSubmit = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const payload: AdminCreateEventRequest = { ...value };
      if (payload.type === 'on-demand') {
        delete payload.start_date;
        delete payload.end_date;
      }

      const response = await fetchApi('POST', `/main/event-create`, payload);
      notifyResponse(response);

      const eventId = (response as any).eventId as number | undefined;
      if (eventId) {
        resetCache(Event.id, String(eventId));
        resetCache(EventExtendedView.id, String(eventId));
        resetCache(Ticket.id, String(eventId));
        navigate(`/admin/event/display/${eventId}`);
      }
    } catch (e) {
      notifyError(e, 'Could not create event');
    }
    setSubmitting(false);
  };

  const title = copyFrom ? 'Copy Event' : 'Create an Event';

  return (
    <Container fluid>
      <PageTitle title={title} subtitle="Create an event using the admin console" />
      {copyFrom && !copyFromRecord.data && <div>Loading event…</div>}
      <EventForm
        submitLabel={copyFrom ? 'Create copy' : 'Create event'}
        submitting={submitting}
        value={value}
        institutionId={institutionId}
        isUpdate={false}
        onChange={setValue}
        onSubmit={handleSubmit}
      />
      <div className="mt-3" />
      <Button variant="outline-secondary" size="sm" onClick={() => navigate(`/admin/event/search`)}>
        Back to search
      </Button>
    </Container>
  );
};
